import { useCallback, useEffect, useRef, useState } from "react";

import { computePosition } from "@floating-ui/dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_NORMAL as NORMAL_PRIORITY,
  SELECTION_CHANGE_COMMAND as ON_SELECTION_CHANGE,
} from "lexical";
import { createPortal } from "react-dom";

import { FloatingToolbar } from "./FloatingToolbar";
import { getPointerState, subscribePointerInteractions } from "./pointerInteractions";

type FloatingMenuCoords = { x: number; y: number } | undefined;

export type FloatingMenuComponentProps = {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  shouldShow: boolean;
};

const OFFSET = 8;

export function FloatingToolbarPlugin() {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<FloatingMenuCoords>(undefined);
  const show = coords !== undefined;

  const [editor] = useLexicalComposerContext();

  const calculatePosition = useCallback(() => {
    const domSelection = getSelection();
    const domRange = domSelection?.rangeCount !== 0 && domSelection?.getRangeAt(0);

    const { isPointerDown } = getPointerState();

    if (!domRange || !ref.current || isPointerDown) {
      return setCoords(undefined);
    }

    computePosition(domRange, ref.current, { placement: "top-start" })
      .then((pos) => {
        setCoords({ x: pos.x, y: pos.y - OFFSET });
      })
      .catch(() => {
        setCoords(undefined);
      });
  }, []);

  const handleSelectionChange = useCallback(() => {
    if (editor.isComposing()) {
      return false;
    }

    if (editor.getRootElement() !== document.activeElement) {
      setCoords(undefined);
      return true;
    }

    const selection = $getSelection();

    if ($isRangeSelection(selection) && !selection.anchor.is(selection.focus)) {
      calculatePosition();
    } else {
      setCoords(undefined);
    }

    return true;
  }, [editor, calculatePosition]);

  useEffect(() => {
    return editor.registerCommand(ON_SELECTION_CHANGE, handleSelectionChange, NORMAL_PRIORITY);
  }, [editor, handleSelectionChange]);

  useEffect(() => {
    // On pointer up, re-evaluate selection/position once.
    return subscribePointerInteractions(({ isPointerUp }) => {
      if (isPointerUp) {
        editor.getEditorState().read(() => {
          handleSelectionChange();
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, handleSelectionChange]);

  return show
    ? createPortal(
        <div
          ref={ref}
          style={{
            position: "absolute",
            top: coords?.y,
            left: coords?.x,
            opacity: 1,
            transition: "opacity 200ms ease-out",
            pointerEvents: "auto",
          }}
          data-test="floating-toolbar"
        >
          <FloatingToolbar editor={editor} />
        </div>,
        document.body
      )
    : null;
}
